// Car-health chip (Recent Sessions card + table) and the per-lap dot (lap list).
//
// Both read the verdict the backend stored at ingest (core/car_health.py):
// `status` is green / amber / red / unchecked and `findings` carry the laps
// each fired on plus one worst sample. `unchecked` gets a deliberately neutral
// chip: a session with no engine channels must not look "fine", but a blank
// reads as broken, so it says "Not checked" and the popover explains why
// (`unchecked` is the rule → reason map).
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './HealthChip.module.css';
import { uncheckedSummary } from './healthChipSummary.js';

const LEVEL_RANK = { info: 0, amber: 1, red: 2 };

export const HEALTH_LABEL = {
  green: 'Car OK',
  amber: 'Car: check',
  red: 'Car: fault',
  unchecked: 'Not checked',
};

export function findingsForLap(findings, lapNumber) {
  return (findings || []).filter(f => (f.laps || []).includes(lapNumber));
}

function sortWorstFirst(findings) {
  return [...(findings || [])].sort(
    (a, b) => (LEVEL_RANK[b.level] ?? 0) - (LEVEL_RANK[a.level] ?? 0),
  );
}

function lapsLabel(f) {
  if (!f.laps || f.laps.length === 0) return 'session';
  if (f.laps.length === 1) return `L${f.laps[0]}`;
  if (f.laps.length <= 4) return f.laps.map(l => `L${l}`).join(', ');
  return `L${f.laps[0]}–L${f.laps[f.laps.length - 1]} (${f.laps.length} laps)`;
}

function summaryTitle(status, findings, unchecked) {
  if (status === 'unchecked') return uncheckedSummary(unchecked).headline;
  const real = sortWorstFirst(findings).filter(f => f.level !== 'info');
  if (status === 'green') {
    const info = (findings || []).filter(f => f.level === 'info');
    return info.length
      ? `No engine issues flagged. ${info.map(f => f.title).join(' · ')}`
      : 'No engine issues flagged in the timed laps';
  }
  return real.map(f => `${f.title} (${lapsLabel(f)})`).join(' · ');
}

// ── Popover ──────────────────────────────────────────────────────────────────
function HealthPopover({ anchorRect, status, findings, unchecked, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const style = { position: 'fixed' };
  if (anchorRect) {
    const w = Math.min(340, window.innerWidth - 16);
    style.width = w;
    style.left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - w - 8));
    style.top = anchorRect.bottom + 6;
    style.maxHeight = Math.max(120, window.innerHeight - anchorRect.bottom - 16);
  }
  const sorted = sortWorstFirst(findings);
  const real = sorted.filter(f => f.level !== 'info');
  const info = sorted.filter(f => f.level === 'info');

  return createPortal(
    <div ref={ref} className={styles.popover} style={style}
         onClick={e => e.stopPropagation()} role="dialog" aria-label="Car health">
      <div className={`${styles.popHead} ${styles[status]}`}>
        <span className={styles.dot} aria-hidden="true" />
        <span>{HEALTH_LABEL[status] || 'Car health'}</span>
        <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
      </div>
      {status === 'green' && real.length === 0 && (
        <div className={styles.ok}>No engine-health issues flagged across the timed laps.</div>
      )}
      {status === 'unchecked' && (() => {
        const { headline, lines } = uncheckedSummary(unchecked);
        return (
          <div className={styles.ok}>
            {lines.length > 1 ? (
              <ul className={styles.skipList}>
                {lines.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            ) : (
              <div>{headline}.</div>
            )}
            <div className={styles.skipNote}>Car health is judged from logged engine channels (oil, coolant, voltage, knock, lambda…). This session logs none it can use.</div>
          </div>
        );
      })()}
      {real.map((f, i) => (
        <div key={i} className={`${styles.finding} ${styles[f.level]}`}>
          <div className={styles.findingHead}>
            <span className={styles.dot} aria-hidden="true" />
            <strong>{f.title}</strong>
            <span className={styles.laps}>{lapsLabel(f)}</span>
          </div>
          <div className={styles.findingText}>{f.text}</div>
        </div>
      ))}
      {info.length > 0 && (
        <div className={styles.infoBlock}>
          {info.map((f, i) => (
            <div key={i} className={styles.infoLine}><em>{f.title}</em> — {f.text}</div>
          ))}
        </div>
      )}
      <div className={styles.foot}>
        {status !== 'unchecked' && 'Judged from the logged engine channels on timed laps only. Not a diagnosis. '}
        <a className={styles.footLink} href="/car-health.html" target="_blank" rel="noopener">How the rules work</a>
      </div>
    </div>,
    document.body,
  );
}

// ── Session chip ─────────────────────────────────────────────────────────────
export default function HealthChip({ status, findings, unchecked, compact = false, className = '' }) {
  const [anchor, setAnchor] = useState(null);
  if (!status) return null;
  const title = summaryTitle(status, findings, unchecked);
  return (
    <>
      <button
        type="button"
        className={`${styles.chip} ${styles[status]} ${compact ? styles.compact : ''} ${className}`}
        title={title}
        aria-label={`${HEALTH_LABEL[status]}: ${title}`}
        onClick={e => {
          e.stopPropagation();
          // Read the rect now: React nulls e.currentTarget once the handler
          // returns, and a functional updater runs after that.
          const rect = e.currentTarget.getBoundingClientRect();
          setAnchor(a => (a ? null : rect));
        }}
      >
        <span className={styles.dot} aria-hidden="true" />
        {!compact && <span className={styles.chipText}>{HEALTH_LABEL[status]}</span>}
      </button>
      {anchor && (
        <HealthPopover anchorRect={anchor} status={status} findings={findings}
                       unchecked={unchecked} onClose={() => setAnchor(null)} />
      )}
    </>
  );
}

// ── Per-lap dot ──────────────────────────────────────────────────────────────
// `level` is the lap's roll-up (amber/red); `findings` are the ones that fired
// on this lap; `onJump` puts the trace cursor on the worst sample.
export function LapHealthDot({ level, findings, onJump }) {
  if (!level) return null;
  const sorted = sortWorstFirst(findings);
  const title = sorted.map(f => f.text).join('\n');
  return (
    <button
      type="button"
      className={`${styles.lapDot} ${styles[level]}`}
      title={title}
      aria-label={`Car health on this lap: ${sorted.map(f => f.title).join(', ')}`}
      onClick={e => { e.stopPropagation(); onJump?.(sorted[0]); }}
    >
      <span className={styles.dot} aria-hidden="true" />
    </button>
  );
}
